import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ConfirmCardSetupData,
  PaymentIntentResult,
  SetupIntentResult,
  Stripe,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js';
import { switchMap, tap } from 'rxjs';
import { CardFormComponent } from '../account/components/card-form/card-form.component';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DataLoaderService } from './data-loader.service';
import { SentryService } from './sentry.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  private stripe: Stripe;
  private config = inject(PICFLOW_CONFIG);
  private analytics = inject(PICFLOW_ANALYTICS);
  private loader = inject(DataLoaderService);
  private http = inject(HttpClient);
  private toasts = inject(ToastService);
  private sentryService = inject(SentryService);

  private async init() {
    return this.loader.get('stripe', async () => this.setKey(this.config.stripePublishableKey));
  }

  private async setKey(key: string) {
    const resp = await import('@stripe/stripe-js');
    this.stripe = await resp.loadStripe(key);
    return true;
  }

  public createCard(cardForm: CardFormComponent) {
    return this.http.post<{ client_secret: string }>(`${this.config.apiEndpoint}/billing/cards`, {}).pipe(
      switchMap(card => {
        return this.confirmCard(card, cardForm);
      }),
      tap(resp => {
        if (!resp.error) this.analytics.track('card_added');
      })
    );
  }

  private async confirmCard(createCardResp: { client_secret: string }, cardForm: CardFormComponent) {
    const resp = await this.confirmCardSetup(createCardResp.client_secret, {
      payment_method: { card: cardForm.cardNumber },
    });

    if (resp.error) {
      console.warn(resp.error);
      if (!['incomplete_number', 'incorrect_cvc'].includes(resp.error.code)) {
        this.sentryService.captureException('confirm-card-failed');
        this.analytics.track('confirm_card_failed', true, {
          code: resp.error.code,
        });
      }
      this.toasts.add(resp.error.message, 'error');
    }
    return resp;
  }

  public async confirmCardSetup(clientSecret: string, options: ConfirmCardSetupData): Promise<SetupIntentResult> {
    await this.init();
    return this.stripe.confirmCardSetup(clientSecret, options);
  }

  public async confirmCardPayment(
    clientSecret: string,
    options: { payment_method: string }
  ): Promise<PaymentIntentResult> {
    await this.init();
    return this.stripe.confirmCardPayment(clientSecret, options);
  }

  public async elements(options: StripeElementsOptionsClientSecret) {
    await this.init();
    return this.stripe.elements(options);
  }
}
